import Dexie, { DexieError } from "dexie";
import {
    AircraftExtended,
    CompanyBaseExtended,
    CompanyExtended,
    CostCodeLookup,
    ExtendTicket,
    JobExtended,
    LocalSetting,
    PersonExtended,
    RiskAssessmentFactorExtended,
    TicketExtended,
    TicketLocationExtended,
} from "./TypeExtensions";
import {
    JobAssociableAssignmentSummary,
    QuantityType,
    Signable,
    SignableResourceType,
    Ticket,
    TicketLineItem,
    TicketLineItemType,
    TicketLocation,
    TicketPax,
} from "../sharedCode/avroGraphql";
import { TicketNumberGenerator } from "./TicketNumberGenerator";
import TicketValidator, { FAILED_SYNC, PARTIAL_SYNC } from "./TicketValidator";

import { DefinitelyAnArray, getLastRevision, getTicketNumber, IsDemoMode } from "../sharedCode/HelperMethods";
import { logSentryError, logSentryFatal } from "./Sentry";
import { LANDING_LINE_ITEM_TYPES } from "../sharedCode/metadata";
import { createInitializationTicket, getTicketValuesFromJob } from "./UtilityMethods";
import dayjs from "dayjs";
import { UseUpDownTimes } from "../Components/inputs/inputHelpers";
import { TicketSort } from "../FlightPackage/TicketNavigator";

export const TICKET_NUMBER_KEY = "ticket_number";
export const UP_DOWN_REQUIRED_KEY = "ticket_style";
export const DEMO_MODE_KEY = "demo_mode";

// extend the class for typescript support : https://dexie.org/docs/Typescript
const DB_NAME = "TripTicketLocal001";

class LocalDatabase extends Dexie {
    aircraft!: Dexie.Table<AircraftExtended, number>;
    ame!: Dexie.Table<PersonExtended, number>;
    company!: Dexie.Table<CompanyExtended, number>;
    companyBase!: Dexie.Table<CompanyBaseExtended, number>;
    job!: Dexie.Table<JobExtended, number>;
    localSetting!: Dexie.Table<LocalSetting, string>;
    // log!: Dexie.Table<LogEntry, number>;
    pilot!: Dexie.Table<PersonExtended, number>;
    pilotAssignments!: Dexie.Table<JobAssociableAssignmentSummary, number>;
    quantityType!: Dexie.Table<QuantityType, number>;
    signableResourceType!: Dexie.Table<SignableResourceType, number>;
    ticket!: Dexie.Table<TicketExtended, number>;
    ticketLineItemType!: Dexie.Table<TicketLineItemType, string>;
    user!: Dexie.Table<PersonExtended, number>;
    riskAssessmentFactor!: Dexie.Table<RiskAssessmentFactorExtended, number>;
    costCodeLookup!: Dexie.Table<CostCodeLookup, string>;

    constructor() {
        super(DB_NAME);

        // we are using dexie 3 so no need to keep the old schemas around
        // this.version(1).stores({
        //     job: "id",
        //     localSetting: "key",
        //     log: "++id",
        //     quantityType: "id",
        //     signableResourceType: "id",
        //     ticket: "id",
        //     riskAssessmentFactor: "id",
        // });

        // this.version(2).stores({
        //     aircraft: "id",
        //     ame: "id",
        //     company: "id",
        //     companyBase: "id",
        //     job: "id",
        //     localSetting: "key",
        //     log: "++id",
        //     pilot: "id",
        //     quantityType: "id",
        //     signableResourceType: "id",
        //     ticket: "id",
        //     ticketLineItemType: "id",
        //     user: "id",
        //     riskAssessmentFactor: "id",
        // });

        // this.version(3)
        //   .stores({
        //       aircraft: "id",
        //       ame: "id",
        //       company: "id",
        //       companyBase: "id",
        //       job: "id",
        //       localSetting: "key",
        //       // log: "++id",
        //       pilot: "id",
        //       pilotAssignments: "++localId",
        //       quantityType: "id",
        //       signableResourceType: "id",
        //       ticket: "id",
        //       ticketLineItemType: "id",
        //       user: "id",
        //       riskAssessmentFactor: "id",
        //       costCodeLookup: "keyHash, company_id",
        //   })
        //   .upgrade(async (txn) => {
        //       const hashedLegs: Array<{ company_id: number | undefined; flightLeg: TicketLocationExtended; ticket: Ticket }> = [];
        //       // calculate hashes for all applicable flight legs
        //       await txn
        //         .table("ticket")
        //         .toCollection()
        //         .modify((ticket: Ticket) => {
        //             ticket.Locations?.forEach((rawLeg) => {
        //                 const flightLeg = ExtendTicketLocation(ticket, rawLeg ?? ({} as TicketLocation));
        //                 flightLeg.keyHash = calculateLookupKeyHash(ticket.company_id, flightLeg);
        //                 flightLeg.valueHash = calculateLookupValueHash(ticket.company_id, flightLeg);
        //                 if (flightLeg.keyHash != null) {
        //                     hashedLegs.push({ company_id: ticket.company_id, flightLeg, ticket });
        //                 }
        //             });
        //         });
        //
        //       const lookupsToSave = new Map();
        //
        //       hashedLegs.forEach((tuple) => {
        //           const existingLookup = lookupsToSave.get(tuple.flightLeg.keyHash);
        //           if (existingLookup) {
        //               existingLookup.usages++;
        //               lookupsToSave.set(tuple.flightLeg.keyHash, existingLookup);
        //           } else {
        //               lookupsToSave.set(tuple.flightLeg.keyHash, {
        //                   keyHash: tuple.flightLeg.keyHash,
        //                   valueHash: tuple.flightLeg.valueHash,
        //                   company_id: tuple.company_id,
        //                   location: tuple.flightLeg?.location,
        //                   cost_code: tuple.flightLeg?.cost_code,
        //                   usages: 1,
        //               });
        //           }
        //       });
        //
        //       // create lookup table
        //       await txn.table("costCodeLookup").bulkAdd(Array.from(lookupsToSave.values()));
        //   });

        this.version(4)
            .stores({
                aircraft: "id",
                ame: "id",
                company: "id",
                companyBase: "id",
                job: "id",
                localSetting: "key",
                // log: "++id",
                pilot: "id",
                pilotAssignments: "++localId",
                quantityType: "id",
                signableResourceType: "id",
                ticket: "id",
                ticketLineItemType: "id",
                user: "id",
                riskAssessmentFactor: "id",
                costCodeLookup: "keyHash, company_id",
            })
            .upgrade(async (txn) => {
                // initialize any_landings and revision_num
                await txn
                    .table("ticket")
                    .toCollection()
                    .each((ticket: Ticket) => {
                        const landingsCount = DefinitelyAnArray(ticket.LineItems).filter(
                            (r: TicketLineItem) => LANDING_LINE_ITEM_TYPES.findIndex((id) => id === r.line_item_type_id) >= 0
                        ).length;

                        const any_landings = landingsCount > 0;
                        const newTicket = {
                            ...ticket,
                            any_landings,
                            revision_num: 0,
                            dateStatus: undefined,
                        } as unknown as TicketExtended;
                        TicketDatabase.ticket.put(newTicket);
                    });
            });

        this.version(5)
            .stores({
                aircraft: "id",
                ame: "id",
                company: "id",
                companyBase: "id",
                job: "id",
                localSetting: "key",
                pilot: "id",
                pilotAssignments: "++localId",
                quantityType: "id",
                signableResourceType: "id",
                ticket: "id",
                ticketLineItemType: "id",
                user: "id",
                riskAssessmentFactor: "id",
                costCodeLookup: "keyHash, company_id",
            })
            .upgrade(async (txn) => {
                await txn
                    .table("ticket")
                    .toCollection()
                    .modify((ticket: Ticket) => {
                        const existingLineItems = DefinitelyAnArray(ticket.LineItems);
                        const filteredLineItems = existingLineItems.filter((lineItem) => lineItem?.line_item_type_id != null);

                        if (existingLineItems.length !== filteredLineItems.length) {
                            ticket.LineItems = filteredLineItems;
                        }
                    });
            });

        this.version(6)
            .stores({
                aircraft: "id",
                ame: "id",
                company: "id",
                companyBase: "id",
                job: "id",
                localSetting: "key",
                pilot: "id",
                pilotAssignments: "++localId",
                quantityType: "id",
                signableResourceType: "id",
                ticket: "id",
                ticketLineItemType: "id",
                user: "id",
                riskAssessmentFactor: "id",
                costCodeLookup: "keyHash, company_id",
            })
            .upgrade(async (txn) => {
                await deleteTicketsPastRetention(txn.table("ticket"));
            });
    }
}

export const TICKET_RETENTION_MONTHS = 18;

export function ticketRetentionCutoff(today: dayjs.Dayjs = dayjs()): dayjs.Dayjs {
    // retain current month tickets plus 18 complete months before that
    return today.startOf("month").subtract(1, "month").subtract(TICKET_RETENTION_MONTHS, "month");
}

/** True when the flight date is before the previous 18 calendar months. The current month is kept in full. Missing or invalid dates are kept. */
export function isTicketPastRetention(date: string | null | undefined, today: dayjs.Dayjs = dayjs()): boolean {
    if (date === null || date === undefined || date === "") {
        return false;
    }
    const ticketDate = dayjs(date);
    if (!ticketDate.isValid()) {
        return false;
    }
    return ticketDate.startOf("day").isBefore(ticketRetentionCutoff(today), "day");
}

export async function deleteTicketsPastRetention(ticketTable: Dexie.Table<Ticket, number>): Promise<void> {
    const today = dayjs();
    const keysToDelete: number[] = [];
    await ticketTable.toCollection().each((ticket: Ticket) => {
        if (isTicketPastRetention(ticket.date, today)) {
            keysToDelete.push(ticket.id);
        }
    });
    if (keysToDelete.length > 0) {
        await ticketTable.bulkDelete(keysToDelete);
    }
}

export const TicketDatabase = new LocalDatabase();

export const newTicket = async (ticketDate: string, job_id: number, pilotId: number): Promise<TicketExtended | undefined> => {
    return TicketDatabase.transaction("rw", TicketDatabase.ticket, TicketDatabase.job, async () => {
        const upDownRequired = UseUpDownTimes();
        const jobList = await TicketDatabase.job.toArray();

        const selectedJob = jobList.find((j) => j.id === job_id);
        if (selectedJob === undefined) {
            throw `couldn't find job: ${ticketDate} ${job_id}`;
        }

        const ticketList = await TicketDatabase.ticket.orderBy("id").toArray();

        const emptyTicket = ExtendTicket({
            id: ticketList.length > 0 && ticketList[0].id < 0 ? ticketList[0].id - 1 : -1,
            revision_num: 0,
            ticket_num: TicketNumberGenerator.new(),
            date: ticketDate,
            up_down_required: upDownRequired ? 1 : 0,
        } as Ticket);

        const jobValues = getTicketValuesFromJob(emptyTicket, selectedJob, pilotId);

        const mergedTicket = { ...emptyTicket, ...jobValues, isDemoTicket: IsDemoMode() };

        const newId = await TicketDatabase.ticket.put(mergedTicket);

        return TicketDatabase.ticket.get(newId);
    }).catch((e) => {
        logSentryFatal(e);
        return Promise.reject(e); // According to the dexie docs this is required for the txn to rollback but my testing showed otherwise. I put it in just in case.
    });
};

export async function reviseTicket(ticketToRevise: TicketExtended): Promise<TicketExtended | undefined> {
    return TicketDatabase.transaction("rw", TicketDatabase.ticket, async () => {
        const ticketList = await TicketDatabase.ticket.orderBy("id").toArray();

        const lastRevision = getLastRevision(ticketList, ticketToRevise);

        if (!lastRevision) {
            throw `unable to revise ticket ${getTicketNumber(ticketToRevise)}    id: ${ticketToRevise.id}`;
        }

        const clonedTicket = JSON.parse(JSON.stringify({ ...ticketToRevise })) as TicketExtended;

        // reset all the child IDs
        let nextLocalId = -1;
        const clonedLineItems =
            clonedTicket.LineItems?.map((i) => {
                return { ...i, id: nextLocalId-- } as TicketLineItem;
            }) ?? [];
        const clonedLocations =
            clonedTicket.Locations?.map((l) => {
                return { ...l, id: nextLocalId-- } as TicketLocation;
            }) ?? [];
        const clonedPax =
            clonedTicket.Pax?.map((p) => {
                return { ...p, id: nextLocalId-- } as TicketPax;
            }) ?? [];

        const newTicket = {
            ...clonedTicket,
            id: ticketList.length > 0 && ticketList[0].id < 0 ? ticketList[0].id - 1 : -1,
            revision_num: lastRevision.revision_num + 1,

            // clear non-ticket info
            OpenedCards: [],
            pushAttempts: 0,
            isDuplicateTicket: false,

            // replace the children
            LineItems: clonedLineItems,
            Locations: clonedLocations,
            Pax: clonedPax,

            // clear signatures
            Signatures: [],
            signature_pending: 0,
            completed_date: undefined,

            nextLocalId: nextLocalId,
        };

        const revisionId = await TicketDatabase.ticket.put({ ...newTicket, isDemoTicket: IsDemoMode() });
        return TicketDatabase.ticket.get(revisionId);
    }).catch((e) => {
        logSentryFatal(e);
        return Promise.reject(e); // According to the dexie docs this is required for the txn to rollback but my testing showed otherwise. I put it in just in case.
    });
}

export const cloneTicket = async (ticketToClone: TicketExtended, ticketDate: string, job_id: number, pilotId: number): Promise<TicketExtended | undefined> => {
    return TicketDatabase.transaction("rw", TicketDatabase.job, TicketDatabase.ticket, async () => {
        const jobList = await TicketDatabase.job.toArray();

        const selectedJob = jobList.find((j) => j.id === job_id);
        if (selectedJob === undefined) {
            throw `couldn't find job: ${ticketDate} ${job_id}`;
        }

        const ticketList = await TicketDatabase.ticket.orderBy("id").toArray();
        // we only have data in the ticket so a deep clone is easy, just stringify then parse to a new object
        const clonedTicket = JSON.parse(JSON.stringify({ ...ticketToClone })) as TicketExtended;
        let nextLocalId = -1;

        const clonedLocations: TicketLocation[] =
            clonedTicket.Locations?.map((l) => {
                return { ...l, id: nextLocalId--, up_time: null, down_time: null, hours: clonedTicket.up_down_required === 1 ? null : l?.hours } as TicketLocation;
            }) ?? [];

        const newTicket = {
            ...clonedTicket,
            id: ticketList.length > 0 && ticketList[0].id < 0 ? ticketList[0].id - 1 : -1,
            revision_num: 0,
            ticket_num: TicketNumberGenerator.new(),
            date: ticketDate,
            job_id: job_id,
            up_time: null,
            down_time: null,
            comments: null,
            Signatures: [],
            LineItems: [],
            hasLocalChanges: false,
            OpenedCards: [],
            pushAttempts: 0,
            isDuplicateTicket: false,
            partialSyncSnapshot: undefined,
            is_per_diems: undefined,
            is_other_charges: undefined,
            any_landings: clonedTicket.is_no_fly === 0 ? undefined : 0,
            signature_pending: undefined,
            completed_date: undefined,
            Locations: clonedLocations,
            nextLocalId: nextLocalId,
        };

        const jobValues = getTicketValuesFromJob(newTicket, selectedJob, pilotId);
        const mergedTicket = { ...newTicket, ...jobValues };
        const newId = await TicketDatabase.ticket.put(mergedTicket);

        return TicketDatabase.ticket.get(newId);
    }).catch((e) => {
        logSentryFatal(e);
        return Promise.reject(e); // According to the dexie docs this is required for the txn to rollback but my testing showed otherwise. I put it in just in case.
    });
};

export async function deleteTicket(idToDelete: number): Promise<number> {
    return TicketDatabase.transaction("rw", TicketDatabase.ticket, async () => {
        const allTickets = await TicketDatabase.ticket.toArray();
        const filteredList = allTickets.filter((t) => t.isDemoTicket === IsDemoMode()).sort(TicketSort);

        const indexToDelete = filteredList.findIndex((t) => t.id === idToDelete);
        const ticketToDelete = indexToDelete < filteredList.length ? filteredList[indexToDelete] : createInitializationTicket();

        const indexToSelect = indexToDelete === 0 ? 1 : indexToDelete - 1;
        const newSelectedTicket = indexToSelect < filteredList.length ? filteredList[indexToSelect] : createInitializationTicket();

        if (!IsDemoMode() && TicketValidator.IsReadOnly(ticketToDelete)) {
            throw "DELETE COMMAND ISSUED FOR READONLY TICKET, PLEASE CONTACT SUPPORT";
        }

        await TicketDatabase.ticket.delete(idToDelete).catch((e) => {
            logSentryError(e as DexieError);
        });

        return newSelectedTicket.id;
    });
}

export const ResetDatabase = (): void => {
    TicketDatabase.close();
    LocalDatabase.delete(DB_NAME).then(() => {
        localStorage.clear();
        window.location.reload();
    });
};

export async function ConvertToExtendedTicket(ticketFromRPS: Ticket): Promise<TicketExtended> {
    return ExtendTicket(ticketFromRPS);
}

export const ResetSyncCounts = async (): Promise<void> => {
    try {
        await TicketDatabase.transaction("rw", TicketDatabase.ticket, async () => {
            const allTickets = await TicketDatabase.ticket.toArray();
            const staleTickets = allTickets.filter((t) => t.pushAttempts === FAILED_SYNC || t.pushAttempts === PARTIAL_SYNC);
            const ticketsToRetry = staleTickets.map((row) => ({ ...row, pushAttempts: 0 }));
            await TicketDatabase.ticket.bulkPut(ticketsToRetry);
        });
    } catch (error) {
        logSentryError(error as DexieError);
    }
};

export const setUpDownRequired = (up_down_required: boolean): void => {
    const localSetting = { key: UP_DOWN_REQUIRED_KEY, value: up_down_required ? "Y" : "N" };

    TicketDatabase.localSetting.put(localSetting);
};

export const getLocalSettingBoolean = (key: string, localSettingList: LocalSetting[], defaultValue = false): boolean => {
    const localSetting = localSettingList.find((s) => s.key === key);

    if (localSetting === undefined) {
        return defaultValue;
    }

    return (localSetting.value ?? "N") === "Y";
};

export async function mergeTicketSignable(ticketId: number, signable: Signable): Promise<void> {
    return TicketDatabase.transaction("rw", TicketDatabase.ticket, async () => {
        await TicketDatabase.ticket.get(ticketId).then((ticket) => {
            if (!ticket) throw new Error(`signable received but no such ticket id ${ticketId}`);

            const newSignableList = [...(ticket.Signatures ?? []), signable];

            const changes = {
                nextLocalId: ticket.nextLocalId--,
                Signatures: newSignableList,
                completed_date: dayjs().toISOString(),
            };

            return TicketDatabase.ticket.update(ticketId, changes).then((updateCount) => {
                if (updateCount === 0) {
                    throw new Error(`signable received but no update on ticket id ${ticketId}`);
                }

                return updateCount;
            });
        });
    }).catch((e) => {
        logSentryFatal(e);
        return Promise.reject(e); // According to the dexie docs this is required for the txn to rollback but my testing showed otherwise. I put it in just in case.
    });
}

export async function addLineItem(ticketId: number, newLineItem: TicketLineItem): Promise<void> {
    return TicketDatabase.transaction("rw", TicketDatabase.ticket, async () => {
        const ticketToUpdate = await TicketDatabase.ticket.get(ticketId);

        const newLineItems = [...(ticketToUpdate?.LineItems ?? []), newLineItem];
        const changes = { nextLocalId: (ticketToUpdate?.nextLocalId ?? 0) - 1, LineItems: newLineItems };

        const updateCount = await TicketDatabase.ticket.update(ticketId, changes);
        if (updateCount !== 1) {
            throw new Error(`failed add line item ticket id :${ticketId}: ${JSON.stringify(newLineItem)}`);
        }

        const savedTicket = await TicketDatabase.ticket.get(ticketId);

        if (!savedTicket) {
            throw new Error("ADDING LINE ITEM TO TICKET FAILED");
        }
    }).catch((e) => {
        logSentryFatal(e);
        return Promise.reject(e); // According to the dexie docs this is required for the txn to rollback but my testing showed otherwise. I put it in just in case.
    });
}

export async function addLocation(ticketId: number, newLocation: TicketLocationExtended): Promise<void> {
    return TicketDatabase.transaction("rw", TicketDatabase.ticket, async () => {
        await TicketDatabase.ticket
            .get(ticketId)
            .then((ticketToUpdate) => {
                const newLocations = [...(ticketToUpdate?.Locations ?? []), newLocation];
                return { nextLocalId: (ticketToUpdate?.nextLocalId ?? 0) - 1, Locations: newLocations };
            })
            .then((changes) => {
                return TicketDatabase.ticket.update(ticketId, changes);
            })
            .then((updateCount) => {
                if (updateCount !== 1) {
                    throw new Error(`failed add location ticket id :${ticketId}: ${JSON.stringify(newLocation)}`);
                }
                return TicketDatabase.ticket.get(ticketId);
            })
            .then((savedTicket) => {
                if (!savedTicket) {
                    throw new Error("ADDING LOCATION TO TICKET FAILED");
                }
            });
    }).catch((e) => {
        logSentryFatal(e);
        return Promise.reject(e); // According to the dexie docs this is required for the txn to rollback but my testing showed otherwise. I put it in just in case.
    });
}

export async function addPassenger(ticketId: number, newPassenger: TicketPax): Promise<void> {
    return TicketDatabase.transaction("rw", TicketDatabase.ticket, async () => {
        await TicketDatabase.ticket
            .get(ticketId)
            .then((ticketToUpdate) => {
                const newPassengers = [...(ticketToUpdate?.Pax ?? []), newPassenger];
                return { nextLocalId: (ticketToUpdate?.nextLocalId ?? 0) - 1, num_pax: newPassengers.length, Pax: newPassengers };
            })
            .then((changes) => {
                return TicketDatabase.ticket.update(ticketId, changes);
            })
            .then((updateCount) => {
                if (updateCount !== 1) {
                    throw new Error(`failed add location ticket id :${ticketId}: ${JSON.stringify(newPassenger)}`);
                }
                return TicketDatabase.ticket.get(ticketId);
            })
            .then((savedTicket) => {
                if (!savedTicket) {
                    throw new Error("ADDING LOCATION TO TICKET FAILED");
                }
            });
    }).catch((e) => {
        logSentryFatal(e);
        return Promise.reject(e); // According to the dexie docs this is required for the txn to rollback but my testing showed otherwise. I put it in just in case.
    });
}

export async function saveTicketChanges(ticketId: number, valuesToMerge: Record<string, unknown>): Promise<void> {
    return TicketDatabase.transaction("rw", TicketDatabase.ticket, async () => {
        const updateCount = await TicketDatabase.ticket.update(ticketId, { ...valuesToMerge, hasLocalChanges: true } as TicketExtended);
        if (updateCount !== 1) {
            throw new Error(`null update ticket id :${ticketId}: ${JSON.stringify(valuesToMerge)}`);
        }
    }).catch((e) => {
        logSentryFatal(e);
        throw e;
    });
}

export async function saveLocalOnlyTicketChanges(ticketId: number, valuesToMerge: Record<string, unknown>): Promise<void> {
    return TicketDatabase.transaction("rw", TicketDatabase.ticket, async () => {
        const updateCount = await TicketDatabase.ticket.update(ticketId, { ...valuesToMerge } as TicketExtended);
        if (updateCount !== 1) {
            throw new Error(`null update ticket id :${ticketId}: ${JSON.stringify(valuesToMerge)}`);
        }
    }).catch((e) => {
        logSentryFatal(e);
        throw e;
    });
}

export async function saveStalledTickets(stalledTickets: TicketExtended[]): Promise<void> {
    return TicketDatabase.transaction("rw", TicketDatabase.ticket, async () => {
        await TicketDatabase.ticket.clear();
        await TicketDatabase.ticket.bulkAdd(stalledTickets);
    });
}
